import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { MessageService, ConfirmationService } from 'primeng/api';
import { BaseComponent } from '../../base/base-component';
import { Router, ActivatedRoute } from '@angular/router';
import { AppMainComponent } from 'src/app/app.main.component';
import { ProfilService } from 'src/app/services/profil-service';
import { Subscription } from 'rxjs';
import { Job } from 'src/app/entites/job';
import { JobService } from 'src/app/services/job.service';
import { finalize } from 'rxjs/operators';
import * as cronstrue from 'cronstrue';
import 'cronstrue/locales/fr';
import { SihcHubService, SihcEvents } from 'src/app/services/sihc-hub.service';
import { DonneesBaseService } from 'src/app/services/donnees-base-service';
import { JobStatut } from 'src/app/entites/enums/job-statut';

@Component({
    selector: 'app-admin-job',
    templateUrl: './admin-job.component.html',
    providers: [ConfirmationService]
})
export class AdminJobComponent extends BaseComponent implements OnInit, OnDestroy {
    jobs = signal<Job[]>([]);
    loading = signal<boolean>(true);
    selectedJob: Job | null = null;
    jobDialog = false;
    errorMessage: string;
    subscription: Subscription = new Subscription();

    constructor(
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private jobService: JobService,
        private jobHub: SihcHubService,
        public sdb: DonneesBaseService,
        public profilService: ProfilService,
        public route: ActivatedRoute,
        public router: Router,
        public appMain: AppMainComponent
    ) {
        super(sdb, profilService, route, router, appMain);
    }

    ngOnInit() {
        this.jobHub.connection.on(SihcEvents.jobsUpdate, (job: Job) => {
            this.jobs.set(this.jobs().map(j => j.jobId === job.jobId ? job : j));
        });
        void this.jobHub.subscribe('jobs').then(() => this.loadJobs());
    }

    ngOnDestroy() {
        this.jobHub.connection.off(SihcEvents.jobsUpdate);
        this.jobHub.unsubscribe('jobs');
        this.subscription.unsubscribe();
    }

    loadJobs() {
        this.loading.set(true);
        this.subscription.add(
            this.jobService.ObtenirJobs().subscribe({
                next: (data) => {
                    this.loading.set(false);
                    this.jobs.set(data);
                    this.errorMessage = null;
                },
                error: (error) => {
                    this.errorMessage = error.message;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erreur',
                        detail: 'Impossible de charger les tâches planifiées',
                        life: 3000
                    });
                }
            })
        );
    }

    toggleJobStatus(job: Job) {
        this.confirmationService.confirm({
            message: `Voulez-vous vraiment ${job.statut === JobStatut.Actif ? 'désactiver' : 'activer'} la tâche planifiée ?`,
            accept: () => {
                this.loading.set(true);
                this.subscription.add(
                    this.jobService.ModifierJob({
                        ...job,
                        statut: job.statut === JobStatut.Actif ? JobStatut.EnPause : JobStatut.Actif
                    })
                        .pipe(finalize(() => this.loading.set(false)))
                        .subscribe({
                            next: (data) => {
                                this.jobs.set(this.jobs().map(j => j.jobId === data.jobId ? data : j));
                                this.messageService.add({
                                    severity: 'success',
                                    summary: 'Succès',
                                    detail: `Tâche planifiée ${job.statut === JobStatut.Actif ? 'désactivée' : 'activée'}`,
                                    life: 3000
                                });
                            },
                            error: () => {
                                this.messageService.add({
                                    severity: 'error',
                                    summary: 'Erreur',
                                    detail: 'Impossible de modifier la tâche planifiée',
                                    life: 3000
                                });
                            }
                        })
                );
            }
        });
    }

    editJob(job: Job) {
        this.selectedJob = { ...job };
        this.jobDialog = true;
    }

    hideDialog() {
        this.jobDialog = false;
        this.selectedJob = null;
    }

    saveJob() {
        if (this.selectedJob) {
            this.updateJob(this.selectedJob);
            this.hideDialog();
        }
    }

    updateJob(job: Job) {
        this.loading.set(true);
        this.subscription.add(
            this.jobService.ModifierJob(job)
                .pipe(finalize(() => this.loading.set(false)))
                .subscribe({
                    next: (data) => {
                        this.jobs.set(this.jobs().map(j => j.jobId === data.jobId ? data : j));
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Succès',
                            detail: 'Tâche planifiée mise à jour',
                            life: 3000
                        });
                    },
                    error: () => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erreur',
                            detail: 'Impossible de mettre à jour la tâche planifiée',
                            life: 3000
                        });
                    }
                })
        );
    }

    forceJob(job: Job) {
        this.confirmationService.confirm({
            message: 'Voulez-vous vraiment forcer l\'exécution de la tâche planifiée ?',
            accept: () => {
                this.loading.set(true);
                this.subscription.add(
                    this.jobService.ForcerJob(job.jobId)
                        .pipe(finalize(() => this.loading.set(false)))
                        .subscribe({
                            next: () => {
                                this.messageService.add({
                                    severity: 'success',
                                    summary: 'Succès',
                                    detail: 'Tâche planifiée forcée',
                                    life: 3000
                                });
                            },
                            error: () => {
                                this.messageService.add({
                                    severity: 'error',
                                    summary: 'Erreur',
                                    detail: 'Impossible de forcer l\'exécution de la tâche planifiée',
                                    life: 3000
                                });
                            }
                        })
                );
            }
        });
    }

    isActif(job: Job): boolean {
        return job.statut === JobStatut.Actif || job.statut === JobStatut.Erreur;
    }

    getStatutLabel(statut: JobStatut): string {
        switch (statut) {
            case JobStatut.EnCours: return 'En cours';
            case JobStatut.EnPause: return 'En pause';
            case JobStatut.Erreur: return 'Erreur';
            default: return 'Actif';
        }
    }

    getStatutSeverity(statut: JobStatut): 'success' | 'info' | 'danger' | 'warn' {
        switch (statut) {
            case JobStatut.EnCours: return 'info';
            case JobStatut.EnPause: return 'danger';
            case JobStatut.Erreur: return 'warn';
            default: return 'success';
        }
    }

    ToReadableCron(cronExpression: string): string {
        const cronRegex = /^[0-9*,-/]+\s[0-9*,-/]+\s[0-9*,-/]+\s[0-9*,-/]+\s[0-9*,-/]+$/;

        if (!cronExpression) {
            return 'N/A';
        }

        if (!cronRegex.test(cronExpression.slice(2))) {
            return 'Expression invalide';
        }

        return cronstrue.toString(cronExpression.slice(2), { locale: 'fr' });
    }

    setSelectedJobActif(actif: boolean) {
        if (this.selectedJob) {
            this.selectedJob.statut = actif ? JobStatut.Actif : JobStatut.EnPause;
        }
    }

    get selectedJobActif(): boolean {
        return this.selectedJob?.statut !== JobStatut.EnPause;
    }

    public get JobStatut(): typeof JobStatut {
        return JobStatut;
    }
}
